// The Alignova icon set. Drawn here rather than pulled from Lucide/Feather so the glyphs share the
// mark's geometry (1.75 stroke, round caps, 24-unit box, generous counters) and so the phone chrome
// stops relying on text glyphs — "▦ ▤ ⚗ ☺ 🔍 🔔" render as a different typeface on every OS and as
// full-colour emoji on iOS, which is why the tab bar never looked like part of the product.
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & {
  /** Pixel size for both axes. Tab bars use 24, inline chips 16–18. */
  size?: number;
  /** Filled variants read as "selected" in the tab bar without changing the silhouette. */
  filled?: boolean;
};

function Svg({ size = 22, filled, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={filled ? 2.1 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* -- navigation ------------------------------------------------------------------------------ */

/** Overview: the lab at a glance — one wide panel over two counters. */
export function IconOverview(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="3.5" width="18" height="7" rx="2" fill={p.filled ? 'currentColor' : 'none'} />
      <rect x="3" y="13.5" width="8" height="7" rx="2" fill={p.filled ? 'currentColor' : 'none'} />
      <rect x="13" y="13.5" width="8" height="7" rx="2" />
    </Svg>
  );
}

/** Board: kanban columns with a card lifted out of the middle lane. */
export function IconBoard(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="3.5" width="5" height="17" rx="1.8" />
      <rect x="9.5" y="3.5" width="5" height="11" rx="1.8" fill={p.filled ? 'currentColor' : 'none'} />
      <rect x="16" y="3.5" width="5" height="14" rx="1.8" />
    </Svg>
  );
}

/** Cases: a stacked record with the top sheet offset. */
export function IconCases(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6 7.5V5.6A1.6 1.6 0 0 1 7.6 4h6.1a1.6 1.6 0 0 1 1.13.47l1.7 1.7" />
      <rect x="3" y="7.5" width="18" height="12.5" rx="2.2" fill={p.filled ? 'currentColor' : 'none'} />
      <path d="M8 12.5h8M8 16h5" stroke={p.filled ? 'var(--c-surface)' : 'currentColor'} />
    </Svg>
  );
}

/** Chat: a conversation, not a speech bubble cliché — two overlapping panes. */
export function IconChat(p: IconProps) {
  return (
    <Svg {...p}>
      <path
        d="M3.5 9.2a3.2 3.2 0 0 1 3.2-3.2h7.6a3.2 3.2 0 0 1 3.2 3.2v3.1a3.2 3.2 0 0 1-3.2 3.2H9.1L5.2 18.4v-2.9a3.2 3.2 0 0 1-1.7-2.8Z"
        fill={p.filled ? 'currentColor' : 'none'}
      />
      <path d="M20.5 11.4v3.4a3 3 0 0 1-1.6 2.6v2.4l-2.9-2" />
    </Svg>
  );
}

/** Inbox / notifications: a tray with an incoming line, instead of the ubiquitous bell. */
export function IconInbox(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.5 13.5 5.9 5.9A2.4 2.4 0 0 1 8.2 4.2h7.6a2.4 2.4 0 0 1 2.3 1.7l2.4 7.6" />
      <path
        d="M3.5 13.5h4.2l1 2.3h6.6l1-2.3h4.2v3.9a2.4 2.4 0 0 1-2.4 2.4H5.9a2.4 2.4 0 0 1-2.4-2.4Z"
        fill={p.filled ? 'currentColor' : 'none'}
      />
    </Svg>
  );
}

/** Profile / account: a shoulder line and head, squared off to match the card radii. */
export function IconProfile(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="8.4" r="3.6" fill={p.filled ? 'currentColor' : 'none'} />
      <path d="M4.8 20.2a7.2 7.2 0 0 1 14.4 0" />
    </Svg>
  );
}

/** Submit / upload records — the clinic portal's primary verb. */
export function IconUpload(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 15.6V4.4M12 4.4 8.1 8.3M12 4.4l3.9 3.9" />
      <path d="M4.5 15.2v2.6a2.4 2.4 0 0 0 2.4 2.4h10.2a2.4 2.4 0 0 0 2.4-2.4v-2.6" />
    </Svg>
  );
}

/** Statement / money. A ledger sheet with a rule, not a dollar sign — the lab bills in PKR. */
export function IconLedger(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="4" y="3.5" width="16" height="17" rx="2.2" />
      <path d="M8 8.4h8M8 12.2h8M8 16h4.5" />
    </Svg>
  );
}

/* -- actions --------------------------------------------------------------------------------- */

export function IconMenu(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h11" />
    </Svg>
  );
}

export function IconClose(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6.2 6.2 17.8 17.8M17.8 6.2 6.2 17.8" />
    </Svg>
  );
}

export function IconSearch(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="m15.4 15.4 4.1 4.1" />
    </Svg>
  );
}

export function IconPlus(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 5.2v13.6M5.2 12h13.6" />
    </Svg>
  );
}

export function IconChevronRight(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="m9.5 5.5 6.4 6.5-6.4 6.5" />
    </Svg>
  );
}

export function IconArrowLeft(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M19 12H5.4M5.4 12l5.4-5.4M5.4 12l5.4 5.4" />
    </Svg>
  );
}

export function IconCheck(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="m5 12.6 4.6 4.6L19 6.8" />
    </Svg>
  );
}

/** Attention: a soft triangle. Used for "action needed", never for ordinary information. */
export function IconAlert(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M10.3 4.2 3.1 17a2 2 0 0 0 1.7 3h14.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9.6v3.9M12 17h.01" />
    </Svg>
  );
}

export function IconClock(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="8.3" />
      <path d="M12 7.4V12l3.1 1.9" />
    </Svg>
  );
}

export function IconPlay(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M8.4 5.9a1 1 0 0 1 1.52-.85l8 6.1a1 1 0 0 1 0 1.7l-8 6.1a1 1 0 0 1-1.52-.85Z" fill="currentColor" />
    </Svg>
  );
}

export function IconPause(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="7" y="5.4" width="3.6" height="13.2" rx="1.4" fill="currentColor" />
      <rect x="13.4" y="5.4" width="3.6" height="13.2" rx="1.4" fill="currentColor" />
    </Svg>
  );
}

export function IconSignOut(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M14.4 4.5H6.9a2.4 2.4 0 0 0-2.4 2.4v10.2a2.4 2.4 0 0 0 2.4 2.4h7.5" />
      <path d="M18.5 12H9.8M18.5 12l-3.3-3.3M18.5 12l-3.3 3.3" />
    </Svg>
  );
}

/** The aligner tray — the product itself. Used by the patient app for tray progress. */
export function IconTray(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4.6 7.4c0-1.7 3.3-2.9 7.4-2.9s7.4 1.2 7.4 2.9c0 3.4-1.4 6.6-3.6 9.2-1 1.2-1.8 2.9-3.8 2.9s-2.8-1.7-3.8-2.9C6 14 4.6 10.8 4.6 7.4Z" />
      <path d="M8.2 8.2c2.5.7 5.1.7 7.6 0" />
    </Svg>
  );
}

/** Reveal a password. An eye, drawn as one continuous lens rather than the usual almond-plus-dot. */
export function IconEye(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M2.6 12S6.2 5.8 12 5.8 21.4 12 21.4 12 17.8 18.2 12 18.2 2.6 12 2.6 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  );
}

/**
 * Hide it again.
 *
 * A struck-through eye rather than a closed one: a closed eye at 16px reads as a smile, and the
 * stroke is what makes "this is currently hidden" legible at a glance.
 */
export function IconEyeOff(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M9.9 5.9A9.6 9.6 0 0 1 12 5.8c5.8 0 9.4 6.2 9.4 6.2a17 17 0 0 1-2.6 3.4M6.3 7.4A16.6 16.6 0 0 0 2.6 12S6.2 18.2 12 18.2a9 9 0 0 0 3.7-.8" />
      <path d="M10 10a3 3 0 0 0 4.1 4.2" />
      <path d="m4 4 16 16" />
    </Svg>
  );
}

/**
 * A 3D model. An isometric box seen from a corner, so the three visible faces read as volume — the
 * flat cube outline every icon set uses is indistinguishable from a square at 16px.
 */
export function IconCube(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 3.2 20 7.6v8.8L12 20.8 4 16.4V7.6L12 3.2Z" />
      <path d="M12 12.4 20 7.9M12 12.4 4 7.9M12 12.4v8.2" />
    </Svg>
  );
}

/** Dates and due-by. A month grid with the top rail marked, not a page-a-day cliché. */
export function IconCalendar(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3.6" y="5.4" width="16.8" height="15" rx="2.4" />
      <path d="M3.6 10.2h16.8M8.4 3.4v3.4M15.6 3.4v3.4" />
      <path d="M8.2 14.2h2M13.8 14.2h2M8.2 17.2h2" />
    </Svg>
  );
}

/** Production: a printed stack coming off the machine. */
export function IconProduction(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M7.2 8.4V4.6a1.2 1.2 0 0 1 1.2-1.2h7.2a1.2 1.2 0 0 1 1.2 1.2v3.8" />
      <rect x="3.4" y="8.4" width="17.2" height="7" rx="2" />
      <path d="M7.2 13.2h9.6v6.2a1.2 1.2 0 0 1-1.2 1.2H8.4a1.2 1.2 0 0 1-1.2-1.2Z" fill={p.filled ? 'currentColor' : 'var(--c-surface)'} />
    </Svg>
  );
}

/** Camera — the patient app's photo check. A body and a lens, no shutter-button cliché. */
export function IconCamera(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.4 8.8a2.2 2.2 0 0 1 2.2-2.2h1.9l1.2-2h6.6l1.2 2h1.9a2.2 2.2 0 0 1 2.2 2.2v8.4a2.2 2.2 0 0 1-2.2 2.2H5.6a2.2 2.2 0 0 1-2.2-2.2Z" />
      <circle cx="12" cy="13" r="3.6" fill={p.filled ? 'currentColor' : 'none'} />
    </Svg>
  );
}

/* -- sidebar navigation ----------------------------------------------------------------------- */

/** Clinics: a building with a door and a cross. */
export function IconClinic(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M4 20.5V6.8a1.5 1.5 0 0 1 .8-1.3l6.5-3.3a1.5 1.5 0 0 1 1.4 0l6.5 3.3a1.5 1.5 0 0 1 .8 1.3v13.7" />
      <path d="M3 20.5h18M10 20.5v-4.5h4v4.5" />
      <path d="M12 8.2v3.6M10.2 10h3.6" />
    </Svg>
  );
}

/** Patients: two people, the front one slightly larger. */
export function IconPeople(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="9.5" cy="8" r="3.2" />
      <path d="M3.5 19.5v-1.2a4.5 4.5 0 0 1 4.5-4.5h3a4.5 4.5 0 0 1 4.5 4.5v1.2" />
      <path d="M16 5.2a3 3 0 0 1 0 5.6M20.5 19.5v-1.2a4 4 0 0 0-2.8-3.8" />
    </Svg>
  );
}

/** Media review: a picture frame with a sun and a hill. */
export function IconImage(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.2" />
      <circle cx="9" cy="9.5" r="1.6" />
      <path d="M20.5 15.5 15.8 11l-6.3 6.2M3.5 17.5l4.2-4" />
    </Svg>
  );
}

/** Field tracking: a map pin. */
export function IconPin(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 21s-6.5-6.1-6.5-11a6.5 6.5 0 0 1 13 0c0 4.9-6.5 11-6.5 11Z" />
      <circle cx="12" cy="10" r="2.4" fill={p.filled ? 'currentColor' : 'none'} />
    </Svg>
  );
}

/** Field team: a route between two stops. */
export function IconRoute(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="6" cy="18" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <path d="M8.5 18H14a3 3 0 0 0 0-6h-4a3 3 0 0 1 0-6h5.5" />
    </Svg>
  );
}

/** Invoices: a sheet with a folded corner and lines. */
export function IconInvoice(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6 3.5h9l4 4v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1Z" />
      <path d="M14.5 3.5v4.2h4.3M8.5 12h7M8.5 15.5h7M12.5 19h3" />
    </Svg>
  );
}

/** Payments: a bank card. */
export function IconCard(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3" y="5.5" width="18" height="13" rx="2.2" />
      <path d="M3 10h18M7 14.5h4" />
    </Svg>
  );
}

/** Expenses: a receipt with a torn foot. */
export function IconReceipt(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6 3.5h12v17l-2.4-1.6-2.4 1.6-1.2-.8-1.2.8-2.4-1.6L6 20.5Z" />
      <path d="M9 8.5h6M9 12h6M9 15.5h3.5" />
    </Svg>
  );
}

/** Price book: a tag with its hole. */
export function IconTag(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M3.5 12.5V5a1.5 1.5 0 0 1 1.5-1.5h7.5l8 8-9 9-8-8Z" />
      <circle cx="8" cy="8" r="1.3" fill={p.filled ? 'currentColor' : 'none'} />
    </Svg>
  );
}

/** Users: a person with a small key. */
export function IconUsers(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="10" cy="8" r="3.4" />
      <path d="M3.5 20v-1a5 5 0 0 1 5-5h3a5 5 0 0 1 3.4 1.3" />
      <circle cx="17.5" cy="16" r="2" />
      <path d="M19 17.5 21 19.5M20 18.5l-1 1" />
    </Svg>
  );
}

/** Notifications: a bell. */
export function IconBell(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 1.5H4.5Z" fill={p.filled ? 'currentColor' : 'none'} />
      <path d="M10 20.5a2 2 0 0 0 4 0" />
    </Svg>
  );
}

/** Settings: a gear drawn as a ring with eight teeth. */
export function IconGear(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M5.5 18.5l1.7-1.7M16.8 7.2l1.7-1.7" />
      <circle cx="12" cy="12" r="6.6" />
    </Svg>
  );
}

/** Roles and permissions: a shield with a tick. */
export function IconShield(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M12 3 4.5 6v5.5c0 4.5 3.2 8.2 7.5 9.5 4.3-1.3 7.5-5 7.5-9.5V6Z" />
      <path d="m9 12 2 2 4-4.5" />
    </Svg>
  );
}

/** Audit log: a clipboard with ticked lines. */
export function IconClipboard(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="5" y="4.5" width="14" height="16" rx="2" />
      <path d="M9 4.5V3.3h6v1.2M8.5 11h1.2l1 1 2-2M13.5 11h2.5M8.5 15.5h1.2l1 1 2-2M13.5 15.5h2.5" />
    </Svg>
  );
}

/** Security: a padlock. */
export function IconLock(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="5" y="10.5" width="14" height="10" rx="2" fill={p.filled ? 'currentColor' : 'none'} />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3M12 14.5v2" />
    </Svg>
  );
}
